import { createNadDebug } from '../../../../utils/debugLog'
import { normalizeRequest, resolveParameterRule } from './inputContractEvaluation'
/** Selectors and legacy media-policy views are projections of the generated
 * model input contract. No native model limits are declared in this module. */

import type { MediaType } from './mentionUtils'
import {
  BYOM_REFERENCE_CAPABILITY_KEYS,
  VIDEO_REFERENCE_POLICY,
  type VideoGeneratorNodeData,
  type VideoReferencePolicy,
} from '../../../../generated/nodeDataTypes'
import {
  MODEL_CAPABILITIES,
  VIDEO_MODEL_CANONICAL_ID,
} from '../../../../generated/modelCapabilities'
import {
  projectQualityForVideoResolution,
  videoResolutionForProjectQuality,
} from '../../videoResolutionProjectQuality'

// Capabilities JSONB values can be string arrays (duration_range, etc.)
// or plain strings (request_pattern).
type CapabilitiesValue = string[] | string
type CapabilitiesMap = Record<string, CapabilitiesValue> | null

/**
 * Reference-media policy for a non-BYOM id the generated mirror doesn't know.
 *
 * Refuses reference media rather than hedging — mirrors the backend
 * `_UNKNOWN_POLICY`. Fail-open belongs to BYOM (user-owned routing), not to a
 * string we cannot resolve to any provider: offering a Video tab for a model
 * nothing can ship turns an up-front "pick a different model" into a
 * mid-generation failure. Mango image caps are explicit; older native models
 * keep null so their existing selector behavior remains unchanged.
 */
const UNKNOWN_REFERENCE_POLICY: VideoReferencePolicy = {
  maxImages: 0,
  maxVideos: 0,
  maxAudios: 0,
  maxTotalReferences: null,
  minVideoSecondsEach: null,
  maxVideoSecondsEach: null,
  maxVideoSecondsCombined: null,
  minAudioSecondsEach: null,
  maxAudioSecondsEach: null,
  maxAudioSecondsCombined: null,
  audioRequiresVisualAnchor: false,
  source: 'unknown',
}

/**
 * Parse a BYOM chip multi-select capability into a maximum count.
 *
 * Mirrors the backend `_parse_count_capability`: an explicitly empty list is a
 * real "none supported" statement (`0`); a missing key or an all-junk list is
 * "not declared" (`null`, fail-open).
 */
function parseCapabilityCount(
  capabilities: CapabilitiesMap | undefined,
  key: string,
): number | null {
  const raw = capabilities?.[key]
  if (raw === undefined || raw === null) return null
  if (!Array.isArray(raw)) return null
  if (raw.length === 0) return 0
  // Strict integer semantics to match the backend's `int(str(item).strip())`.
  // Number.parseInt would accept '3abc' as 3 and '1.5' as 1, so the two sides
  // could derive DIFFERENT caps from the same stored capability list.
  const values = raw
    .map((entry) => String(entry).trim())
    .filter((entry) => /^[+-]?\d+$/.test(entry))
    .map((entry) => Number(entry))
  return values.length > 0 ? Math.max(...values) : null
}

/* ── Types ── */

export interface VideoConstraints {
  /** Available resolution options, or null if resolution is a static label. */
  resolutionOptions: string[] | null
  /** Default resolution for this model. */
  resolutionDefault: string
  /** Static resolution label when resolutionOptions is null. */
  resolutionLabel?: string
  /** Available duration options (seconds), or null if duration is a static label. */
  durationOptions: number[] | null
  /** Default duration for this model+resolution combo. */
  durationDefault: number
  /** Static duration label when durationOptions is null (e.g. "8s"). */
  durationLabel?: string
  /** The provider derives duration from source media; the numeric node value is ignored. */
  durationProviderSelected?: boolean
}

/* ── Main Functions ── */

/**
 * Resolve a model's reference-media policy.
 *
 * Native models read the generated mirror of the backend SoT. BYOM models
 * derive from their declared `reference_*_support` capabilities; an undeclared
 * key yields `null` so user-owned routing stays fail-open (CLAUDE.md #8) and
 * the provider's own validation error surfaces (CLAUDE.md #7).
 */
export function getVideoReferencePolicy(
  model: string,
  capabilities?: CapabilitiesMap,
): VideoReferencePolicy {
  const native = VIDEO_REFERENCE_POLICY[model]
  if (native) return native
  if (isByomModel(model)) {
    return {
      maxImages: parseCapabilityCount(capabilities, BYOM_REFERENCE_CAPABILITY_KEYS.images),
      maxVideos: parseCapabilityCount(capabilities, BYOM_REFERENCE_CAPABILITY_KEYS.videos),
      maxAudios: parseCapabilityCount(capabilities, BYOM_REFERENCE_CAPABILITY_KEYS.audios),
      maxTotalReferences: Array.isArray(capabilities?.max_total_references) && capabilities.max_total_references.length === 0
        ? null : parseCapabilityCount(capabilities, 'max_total_references'),
      minVideoSecondsEach: null,
      maxVideoSecondsEach: null,
      maxVideoSecondsCombined: null,
      minAudioSecondsEach: null,
      maxAudioSecondsEach: null,
      maxAudioSecondsCombined: null,
      // A BYOM registration cannot declare the anchor rule, and inventing one
      // would refuse a request the user's endpoint might well accept
      // (CLAUDE.md #8). The provider owns that call.
      audioRequiresVisualAnchor: false,
      source: 'byom',
    }
  }
  return UNKNOWN_REFERENCE_POLICY
}

/** Max reference images, or `null` for no cap. `0` means unsupported. */
export function getReferenceImageLimit(
  model: string,
  capabilities?: CapabilitiesMap,
): number | null {
  return getVideoReferencePolicy(model, capabilities).maxImages
}

/** Max reference videos, or `null` for no cap. `0` means unsupported. */
export function getReferenceVideoLimit(
  model: string,
  capabilities?: CapabilitiesMap,
): number | null {
  return getVideoReferencePolicy(model, capabilities).maxVideos
}

/** Max reference audio clips, or `null` for no cap. `0` means unsupported. */
export function getReferenceAudioLimit(
  model: string,
  capabilities?: CapabilitiesMap,
): number | null {
  return getVideoReferencePolicy(model, capabilities).maxAudios
}

/**
 * Combined reference cap across all media types, or `null` for none.
 *
 * Distinct from the per-media caps: Seedance 2.0 r2v allows 9 images AND 3
 * audio clips but only 12 references in total, so a selector capped at the
 * image limit alone would refuse a selection the model accepts.
 */
export function getReferenceTotalLimit(
  model: string,
  capabilities?: CapabilitiesMap,
): number | null {
  return getVideoReferencePolicy(model, capabilities).maxTotalReferences
}

/**
 * Total used by ReferenceSelector, or `null` when a known model explicitly
 * declares no combined cap.
 *
 * Unknown native ids keep the historical fallback. BYOM registrations have no
 * combined-cap field, so sum fully-declared per-media maxima; if any visible
 * medium is undeclared, retain the selector's existing high fail-open ceiling
 * instead of silently reducing user routing.
 */
export function getReferenceSelectorTotalLimit(
  model: string,
  capabilities: CapabilitiesMap,
  nativeFallback: number,
  undeclaredByomFallback = 99,
): number | null {
  const policy = getVideoReferencePolicy(model, capabilities)
  if (policy.maxTotalReferences !== null) return policy.maxTotalReferences
  if (VIDEO_REFERENCE_POLICY[model]) return null
  if (policy.source !== 'byom') return nativeFallback

  const mediaLimits = [policy.maxImages, policy.maxVideos, policy.maxAudios]
  if (mediaLimits.some(limit => limit === null)) return undeclaredByomFallback
  return mediaLimits.reduce<number>((total, limit) => total + (limit ?? 0), 0)
}

export function supportsAudioReferenceSelection(
  model: string,
  capabilities?: CapabilitiesMap,
): boolean {
  return getReferenceAudioLimit(model, capabilities) !== 0
}

/**
 * Whether this model needs an image/video reference alongside audio.
 *
 * Seedance documents the rule; the console surfaces it so an audio-only
 * selection is refused before Generate rather than at the provider.
 */
export function audioRequiresVisualAnchor(
  model: string,
  capabilities?: CapabilitiesMap,
): boolean {
  return getVideoReferencePolicy(model, capabilities).audioRequiresVisualAnchor
}

export function supportsVideoReferenceSelection(
  model: string,
  capabilities?: CapabilitiesMap,
): boolean {
  return getReferenceVideoLimit(model, capabilities) !== 0
}

export function getVideoGeneratorReferenceSelectorTabs(
  model: string,
  capabilities?: CapabilitiesMap,
): MediaType[] {
  const tabs: MediaType[] = []
  if (getReferenceImageLimit(model, capabilities) !== 0) tabs.push('image')
  if (supportsVideoReferenceSelection(model, capabilities)) tabs.push('video')
  if (supportsAudioReferenceSelection(model, capabilities)) tabs.push('audio')
  return tabs
}

export function getVideoGeneratorReferenceLimits(
  model: string,
  capabilities?: CapabilitiesMap,
): Partial<Record<MediaType, number>> | undefined {
  const imageLimit = getReferenceImageLimit(model, capabilities)
  const videoLimit = getReferenceVideoLimit(model, capabilities)
  const audioLimit = getReferenceAudioLimit(model, capabilities)

  const limits: Partial<Record<MediaType, number>> = {}
  if (imageLimit !== null) limits.image = imageLimit
  if (videoLimit !== null && videoLimit > 0) limits.video = videoLimit
  if (audioLimit !== null && audioLimit > 0) limits.audio = audioLimit
  return Object.keys(limits).length > 0 ? limits : undefined
}

/**
 * True when the provider derives the clip duration itself (Seedance 2.5
 * Edit/Extend send Atlas `duration: -1`), so the stored `in_duration` is
 * not what will be generated and must be shown as "Auto".
 */
export function isProviderSelectedDuration(model: string): boolean {
  const contract = MODEL_CAPABILITIES[normalizeNativeVideoModel(model)]?.inputContract
  return contract !== undefined && resolveParameterRule(contract, 'duration', {
    modelId: model, inputCounts: {},
  })?.disposition === 'provider-selected'
}

/**
 * Get available options and defaults for a given model and resolution.
 *
 * - Seedance: provider-declared resolutions and integer duration range.
 * - Veo 720p: resolution selectable [720p, 1080p, 4k], duration {4,6,8}, default 8s.
 * - Veo 1080p/4k: resolution selectable, duration fixed 8s.
 */
export function getVideoConstraints(model: string, resolution?: string, referenceImageCount = 0): VideoConstraints {
  const native = MODEL_CAPABILITIES[normalizeNativeVideoModel(model)]
  if (!native || native.modality !== 'video') throw new Error(`Missing video input contract: ${model}`)
  const contract = native.inputContract
  const request = normalizeRequest(contract, {
    modelId: model,
    inputCounts: { in_images: referenceImageCount, in_reference_videos: 0, in_reference_audios: 0 },
    parameters: { ...(resolution ? { resolution } : {}), reference_mode: referenceImageCount > 0 },
  })
  const resolutionRule = resolveParameterRule(contract, 'resolution', request)
  const durationRule = resolveParameterRule(contract, 'duration', request)
  if (!durationRule) throw new Error(`Missing duration contract: ${model}`)
  if (!resolutionRule) throw new Error(`Missing resolution contract: ${model}`)
  createNadDebug('dw:model-input-contract', { priority: true })({ event: 'model-input-contract:controls',
    validation_boundary: 'console-video-controls', model_id: model,
    contract_fingerprint: native.inputContractFingerprint, schema_version: contract.schema_version,
    decision: 'projection', parameters: request.parameters ? { ...request.parameters } : {},
    duration_disposition: durationRule.disposition, duration_default: durationRule.default,
    resolution_default: resolutionRule.default })
  const durationDefault = durationRule.default
  if (typeof durationDefault !== 'number') throw new Error(`Missing duration default: ${model}`)
  const resolutionDefault = resolutionRule?.default
  if (typeof resolutionDefault !== 'string') throw new Error(`Missing resolution default: ${model}`)
  const durations = durationRule.allowed.filter((value): value is number => typeof value === 'number')
  if (durations.length === 0 && durationRule.minimum !== null && durationRule.maximum !== null && durationRule.step !== null) {
    for (let value = durationRule.minimum; value <= durationRule.maximum; value += durationRule.step) durations.push(value)
  }
  const providerSelected = durationRule.disposition === 'provider-selected'
  const fixedDuration = durations.length === 1
  const resolutions = resolutionRule.allowed.filter((value): value is string => typeof value === 'string')
  const fixedResolution = resolutionRule.disposition === 'provider-selected'
  const normalizedResolution = request.parameters?.resolution
  return {
    resolutionOptions: fixedResolution ? null : resolutions,
    resolutionDefault: typeof normalizedResolution === 'string' && resolutions.includes(normalizedResolution)
      ? normalizedResolution : resolutionDefault,
    ...(fixedResolution ? { resolutionLabel: resolutionDefault } : {}),
    durationOptions: providerSelected || fixedDuration ? null : durations,
    durationDefault,
    ...(providerSelected ? { durationProviderSelected: true } : {}),
    ...(fixedDuration ? { durationLabel: `${durationDefault}s` } : {}),
  }
}

/** Picker interaction chooses a legal new value; backend requests are never clamped. */
export function snapOnModelChange(fromModel: string, toModel: string,
  current: { duration: number; resolution: string }, byomCapabilities?: CapabilitiesMap,
): { duration: number; resolution: string } {
  if (normalizeNativeVideoModel(fromModel) === normalizeNativeVideoModel(toModel)) return current
  if (isByomModel(toModel)) {
    const projectQuality = projectQualityForVideoResolution(fromModel, current.resolution)
    const genericCurrent = projectQuality === current.resolution
      ? current
      : { ...current, resolution: projectQuality }
    return snapToByomConstraints(byomCapabilities ?? null, genericCurrent)
  }
  const initial = getVideoConstraints(toModel)
  const currentProjectQuality = projectQualityForVideoResolution(fromModel, current.resolution)
  const mappedResolution = videoResolutionForProjectQuality(toModel, currentProjectQuality)
  const resolution = initial.resolutionOptions?.includes(mappedResolution)
    ? mappedResolution
    : initial.resolutionDefault
  const selected = getVideoConstraints(toModel, resolution)
  return { resolution, duration: selected.durationProviderSelected ? current.duration
    : selected.durationOptions ? snapToNearest(current.duration, selected.durationOptions) : selected.durationDefault }
}

export function snapOnResolutionChange(model: string, newResolution: string, currentDuration: number): number {
  if (!MODEL_CAPABILITIES[normalizeNativeVideoModel(model)]) return currentDuration
  const selected = getVideoConstraints(model, newResolution)
  return selected.durationProviderSelected ? currentDuration
    : selected.durationOptions ? snapToNearest(currentDuration, selected.durationOptions) : selected.durationDefault
}

/**
 * The only resolution a native model can produce, or `null` when it honours the
 * node's choice. `adaptive` for Omni 1.1 Flash Extend (output follows the source
 * video); the declared tier for models whose resolution rule is
 * `provider-selected` (Omni Flash 1.0, Mango, Muse Video), because the provider
 * discards the value and always returns that tier. BYOM is user-owned routing,
 * so it never has one. Mirrors `fixed_native_resolution` in
 * `core/providers/video_param_norm.py`.
 */
export function fixedVideoResolution(model: string): string | null {
  if (isByomModel(model)) return null
  const native = MODEL_CAPABILITIES[normalizeNativeVideoModel(model)]
  if (!native || native.modality !== 'video') return null
  if (native.resolutions.length === 1 && native.resolutions[0] === 'adaptive') return 'adaptive'
  const rule = resolveParameterRule(native.inputContract, 'resolution', { modelId: model, inputCounts: {} })
  return rule?.disposition === 'provider-selected' && typeof rule.default === 'string' ? rule.default : null
}

/**
 * The resolution a node actually sends. A model that can produce only one
 * resolution sends it, whatever the node stored: nodes are stamped with the
 * project's Output Quality (1080p for new projects), which Omni Flash 1.0 and
 * the other fixed-tier models cannot honour. Mirrors `_resolution_for_model` in
 * the video generator agent.
 */
export function effectiveVideoResolution(model: string, stored: string): string {
  return fixedVideoResolution(model) ?? stored
}

export function getNativeVideoAspectRatios(model: string): string[] {
  const normalizedModel = normalizeNativeVideoModel(model)
  return [...(MODEL_CAPABILITIES[normalizedModel]?.aspectRatios ?? ['16:9', '9:16'])]
}

/**
 * The aspect ratio to persist when switching models, or `undefined` to keep
 * the current one.
 *
 * `snapOnModelChange` handles duration and resolution but never touched the
 * aspect ratio, because until source-video models arrived every video model
 * offered a real ratio choice and the user's pick stayed legal. The
 * adaptive-only entries broke that: switching to Omni 1.1 Edit persists
 * `'adaptive'`, and switching back to a ratio-bound model left it there — so
 * the flyout rendered that model's ratios with NONE selected while the request
 * still carried `aspect_ratio="adaptive"`, which the model does not declare.
 *
 * Fails open on purpose. BYOM is user-owned routing (CLAUDE.md #8) and an
 * undeclared model has no capability record, so neither gets a value invented
 * for it — an unknown ratio is the provider's call, not ours.
 */
export function snapAspectRatioOnModelChange(
  toModel: string,
  currentAspectRatio: string,
): string | undefined {
  if (isByomModel(toModel)) return undefined
  const caps = MODEL_CAPABILITIES[normalizeNativeVideoModel(toModel)]
  if (!caps || caps.aspectRatiosOpenEnded) return undefined

  const offered = caps.offeredAspectRatios.length > 0 ? caps.offeredAspectRatios : caps.aspectRatios
  if (offered.length === 0 || offered.includes(currentAspectRatio)) return undefined
  return caps.offeredAspectRatioDefault ?? offered[0]
}

/**
 * Whether a second connected image is the clip's closing frame on `model`.
 *
 * True for frames-only video models: they take a start image and an optional
 * or required end image, and no general reference images, so image 1 opens the
 * clip and image 2 closes it in connection order (RFC-1663 A1). Reads the
 * generated capability record.
 */
export function secondImageClosesClip(model: string): boolean {
  const accepted = MODEL_CAPABILITIES[VIDEO_MODEL_CANONICAL_ID[model] ?? model]?.acceptedInputs ?? []
  return accepted.includes('start-image')
    && (accepted.includes('end-image') || accepted.includes('end-image-optional'))
    && !accepted.includes('image')
}

/**
 * Whether the node should note that image 1 opens the clip and image 2 closes it:
 * a frames-only model with exactly two images in a provider reference mode.
 * MOTION_IMAGE is a local render and FIRST_LAST names the frames already
 * (RFC-1663 A1). An unset mode is the schema default, REFERENCE.
 */
export function showsFirstLastByOrderNotice(
  model: string,
  mode: string | undefined,
  imageCount: number,
): boolean {
  return ['REFERENCE', 'REFERENCE_TO_VIDEO'].includes(mode ?? 'REFERENCE')
    && imageCount === 2
    && secondImageClosesClip(model)
}

/**
 * The generation mode a model switch forces, or `undefined` to keep the
 * current one. Models whose request shape fixes the frame roles get the only
 * mode they can run; everything else keeps the user's choice.
 */
export function modeOnModelChange(
  nextModel: string,
): NonNullable<VideoGeneratorNodeData['in_mode']> | undefined {
  if (
    nextModel === 'seedance-2.5-image-to-video-atlas-cloud'
    // Omni 1.1 I2V takes its single image as the first frame. REFERENCE
    // sends it as an ordinary image part; FIRST_LAST would ask for a last
    // frame, which the Omni request shape rejects.
    || nextModel === 'gemini-omni-1.1-flash-i2v'
    // Muse has no end-frame role: FIRST_LAST would send image 2 as a last
    // frame the provider rejects. Image 1 is the opening frame.
    || nextModel === 'muse-video-1.0'
    // Meta-hosted H3 I2V takes one image as frame 0 and has no closing frame
    // (RFC-1663).
    || nextModel === 'minimax-h3-image-to-video-meta-hosted'
  ) {
    return 'REFERENCE'
  }
  if (nextModel === 'seedance-2.5-first-last-frame-atlas-cloud') return 'FIRST_LAST'
  if (
    [
      'seedance-2.5-reference-to-video-atlas-cloud',
      'seedance-2.5-video-editing-atlas-cloud',
      'seedance-2.5-video-extension-atlas-cloud',
      'gemini-omni-1.1-flash-edit',
      'gemini-omni-1.1-flash-extend',
      'muse-video-1.0-r2v',
      // MiniMax H3 reference-to-video has no frame roles: a First/Last pair
      // would be sent as two ordinary references (RFC-1653).
      'minimax-h3-reference-to-video',
      'minimax-h3-reference-to-video-atlas-cloud',
      'minimax-h3-fast-reference-to-video-atlas-cloud',
      'minimax-h3-reference-to-video-meta-hosted',
    ].includes(nextModel)
  ) {
    return 'REFERENCE_TO_VIDEO'
  }
  return undefined
}

/* ── BYOM (Bring Your Own Model) ── */

/**
 * Build VideoConstraints from a BYOM model's capabilities JSONB.
 *
 * Capabilities is a Record<string, string[]> where:
 * - `resolutions`: e.g. ['480p', '720p', '1080p']
 * - `duration_range`: e.g. ['5', '6', '7', '8', '9', '10'] (string numbers)
 * - `aspect_ratios`: e.g. ['16:9', '9:16']
 *
 * Missing or malformed axes stay unknown and render the authored value as a
 * static label. Unknown provider metadata must never manufacture a value that
 * can flow back into persisted node config.
 */
export function getByomConstraints(
  capabilities: CapabilitiesMap,
  current?: { duration: number; resolution: string },
): VideoConstraints {
  const resolutions = parseStringOptions(capabilities?.resolutions)
  const durations = parseDurationRange(capabilities?.duration_range)
  const currentResolution = current?.resolution ?? ''
  const currentDuration = current?.duration ?? 0

  return {
    resolutionOptions: resolutions,
    resolutionDefault: resolutions?.[0] ?? currentResolution,
    resolutionLabel: resolutions ? undefined : currentResolution,
    durationOptions: durations,
    durationDefault: durations?.[durations.length - 1] ?? currentDuration,
    durationLabel: durations ? undefined : `${currentDuration}s`,
  }
}

/**
 * Get aspect ratio options from BYOM capabilities.
 * Returns null for non-BYOM models (use the static ASPECT_RATIO_OPTIONS).
 */
export function getByomAspectRatios(
  capabilities: CapabilitiesMap,
  currentAspectRatio?: string,
): string[] {
  return parseStringOptions(capabilities?.aspect_ratios)
    ?? (currentAspectRatio ? [currentAspectRatio] : [])
}

/** Returns true if the model ID represents a user-registered BYOM model. */
export function isByomModel(model: string): boolean {
  return model.startsWith('gateway/byom/')
}

/**
 * Snap resolution and duration when switching to a BYOM model.
 */
export function snapToByomConstraints(
  capabilities: CapabilitiesMap,
  current: { duration: number; resolution: string },
): { duration: number; resolution: string } {
  const validResolutions = parseStringOptions(capabilities?.resolutions)
  const resolution = !validResolutions || validResolutions.includes(current.resolution)
    ? current.resolution
    : validResolutions[0]

  const validDurations = parseDurationRange(capabilities?.duration_range)
  const duration = !validDurations || validDurations.includes(current.duration)
    ? current.duration
    : snapToNearest(current.duration, validDurations)

  if (resolution === current.resolution && duration === current.duration) return current

  return { resolution, duration }
}

/** Accept only a complete, non-empty string option declaration. */
function parseStringOptions(value: CapabilitiesValue | undefined): string[] | null {
  if (!Array.isArray(value) || value.length === 0) return null
  const normalized = value.map((entry) => entry.trim())
  if (normalized.some((entry) => entry.length === 0)) return null
  return [...new Set(normalized)]
}

/** Parse a strict positive-integer duration declaration, or keep it unknown. */
function parseDurationRange(value: CapabilitiesValue | undefined): number[] | null {
  if (!Array.isArray(value) || value.length === 0) return null
  if (value.some((entry) => !/^\d+$/.test(entry.trim()))) return null
  const durations = value.map((entry) => Number(entry.trim()))
  if (durations.some((duration) => !Number.isSafeInteger(duration) || duration <= 0)) return null
  return [...new Set(durations)].sort((a, b) => a - b)
}

/* ── Helpers ── */

function normalizeNativeVideoModel(model: string): string {
  return VIDEO_MODEL_CANONICAL_ID[model] ?? model
}

/**
 * Find the nearest value in a sorted array. On ties (equal distance to two values),
 * prefer the lower value (strict < comparison).
 */
function snapToNearest(value: number, options: readonly number[]): number {
  let best = options[0]
  let bestDist = Math.abs(value - best)

  for (let i = 1; i < options.length; i++) {
    const dist = Math.abs(value - options[i])
    if (dist < bestDist) {
      best = options[i]
      bestDist = dist
    }
  }

  return best
}
